// dynamicFilterAdapter.js
//
// Compiles ag-grid serverSide row model filterModel / sortModel into the
// backend's DynamicFilterGroup[] / DynamicSortCriteria[] contract.
//
// Design summary:
// - Each colDef may declare `dynamicFilter` / `dynamicSort`:
//     undefined                -> default transformer (see below)
//     { objectPath, targetType, logic, property } -> declarative remap
//     (rawModel, colDef) => group|group[]|null     -> full custom logic
// - Columns *without* dynamicFilter are left untouched here so the caller
//   can keep routing them through the legacy flat `filter` object during
//   the gradual migration. `consumedColIds` tells the caller which keys
//   were handled by this module so they aren't double-applied.
// - Sorting has no legacy contract to preserve, so every sorted column
//   (configured or not) is compiled here.
// - Lookups are keyed by colId (colDef.colId ?? colDef.field), because
//   ag-grid's filterModel/sortModel are keyed by colId, and two columns
//   can legitimately share the same `field` (see buildColumnLookup).

export const FilterOperator = {
  Equals: 'Equals',
  NotEquals: 'NotEquals',
  Contains: 'Contains',
  StartsWith: 'StartsWith',
  EndsWith: 'EndsWith',
  GreaterThan: 'GreaterThan',
  LessThan: 'LessThan',
  GreaterThanOrEqual: 'GreaterThanOrEqual',
  LessThanOrEqual: 'LessThanOrEqual',
  Blank: 'Blank',
  NotBlank: 'NotBlank',
}

export const FilterLogic = { And: 'And', Or: 'Or' }
export const FilterTargetType = { Object: 'Object', Collection: 'Collection' }
export const SortType = { Asc: 'Asc', Desc: 'Desc' }

// ag-grid "type" -> backend FilterOperator. Extend as new filter comps appear.
const AG_OPERATOR_MAP = {
  equals: FilterOperator.Equals,
  notEqual: FilterOperator.NotEquals,
  contains: FilterOperator.Contains,
  notContains: FilterOperator.NotEquals,
  startsWith: FilterOperator.StartsWith,
  endsWith: FilterOperator.EndsWith,
  greaterThan: FilterOperator.GreaterThan,
  lessThan: FilterOperator.LessThan,
  greaterThanOrEqual: FilterOperator.GreaterThanOrEqual,
  lessThanOrEqual: FilterOperator.LessThanOrEqual,
  blank: FilterOperator.Blank,
  notBlank: FilterOperator.NotBlank,
}

export function mapAgOperatorToBackend(agType) {
  return AG_OPERATOR_MAP[agType] || FilterOperator.Equals
}

export function getColId(colDef) {
  return colDef.colId || colDef.field
}

/**
 * Flattens columnDefs (including grouped/children columns) into a
 * Map<colId, colDef>. Warns (does not throw) on duplicate colIds so
 * columns sharing a `field` without an explicit `colId` are caught early.
 */
export function buildColumnLookup(columnDefs) {
  const map = new Map()
  const seen = new Set()

  const visit = (col) => {
    if (col.children) {
      col.children.forEach(visit)
      return
    }
    const colId = getColId(col)
    if (!colId) return
    if (seen.has(colId)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[dynamicFilterAdapter] Duplicate colId/field "${colId}" detected. ` +
        `Set an explicit unique "colId" on these columns, or server-side filter/sort ` +
        `will only ever resolve one of them.`
      )
    }
    seen.add(colId)
    map.set(colId, col)
  }

    ; (columnDefs || []).forEach(visit)
  return map
}

function capitalizeSegment(segment) {
  return segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : segment
}

function capitalizePath(path) {
  return path.split('.').map(capitalizeSegment).join('.')
}

/**
 * Splits `serverFieldName` (or falls back to `field`) into an ObjectPath /
 * Property pair using dot-path convention:
 *   'code'              -> { objectPath: '',            property: 'Code' }   (own property of the entity)
 *   'factorBunch.name'  -> { objectPath: 'FactorBunch', property: 'Name' }   (nested object's property)
 */
export function resolveServerFieldName(colDef) {
  const path = colDef.serverFieldName || colDef.field
  if (!path) return { objectPath: '', property: '' }
  const idx = path.lastIndexOf('.')
  if (idx === -1) return { objectPath: '', property: capitalizeSegment(path) }
  return {
    objectPath: capitalizePath(path.slice(0, idx)),
    property: capitalizeSegment(path.slice(idx + 1)),
  }
}

// Maps this app's custom date filter component's `type` -> backend operator.
// Model shape: { filter, filter2, filterType: 'date', type: 'greaterThan'|'lessThan'|'equals'|'between' }
const DATE_TYPE_OPERATOR_MAP = {
  greaterThan: FilterOperator.GreaterThan,
  lessThan: FilterOperator.LessThan,
  equals: FilterOperator.Equals,
}

function formatStartOfDay(dateStr) {
  if (!dateStr && dateStr !== 0) return ''
  const str = String(dateStr).trim()
  if (!str) return ''
  const datePart = str.split(/[T\s]/)[0]
  return datePart ? `${datePart}T00:00:00` : str
}

function formatEndOfDay(dateStr) {
  if (!dateStr && dateStr !== 0) return ''
  const str = String(dateStr).trim()
  if (!str) return ''
  const datePart = str.split(/[T\s]/)[0]
  return datePart ? `${datePart}T23:59:59` : str
}

/**
 * Builds DynamicFilterCriteria[] for the custom date filter's model shape.
 * 'between' becomes two AND'd criteria (GreaterThanOrEqual filter at 00:00:00,
 * LessThanOrEqual filter2 at 23:59:59); the other three types map straight through.
 * Returns [] if the model is incomplete (e.g. 'between' picked but filter2 not yet typed).
 */
function buildCriteriaFromDateModel(property, filterModel) {
  if (!filterModel || !filterModel.filter) return []

  if (filterModel.type === 'between') {
    if (!filterModel.filter2) return []
    return [
      { property, operator: FilterOperator.GreaterThanOrEqual, value: formatStartOfDay(filterModel.filter) },
      { property, operator: FilterOperator.LessThanOrEqual, value: formatEndOfDay(filterModel.filter2) },
    ]
  }

  const operator = DATE_TYPE_OPERATOR_MAP[filterModel.type]
  if (!operator) return []
  return [{ property, operator, value: String(filterModel.filter) }]
}

/**
 * Builds DynamicFilterCriteria[] from a single (non-combined) ag-grid
 * filter model. serverSide row model is configured with
 * maxNumConditions: 1, so we never need to handle condition1/condition2.
 */
function buildCriteriaFromSimpleModel(property, filterModel) {
  if (!filterModel) return []

  if (filterModel.type === 'inRange') {
    const criteria = []
    if (filterModel.filter !== undefined && filterModel.filter !== null) {
      criteria.push({ property, operator: FilterOperator.GreaterThanOrEqual, value: String(filterModel.filter) })
    }
    if (filterModel.filterTo !== undefined && filterModel.filterTo !== null) {
      criteria.push({ property, operator: FilterOperator.LessThanOrEqual, value: String(filterModel.filterTo) })
    }
    if (filterModel.dateFrom) {
      criteria.push({ property, operator: FilterOperator.GreaterThanOrEqual, value: String(filterModel.dateFrom) })
    }
    if (filterModel.dateTo) {
      criteria.push({ property, operator: FilterOperator.LessThanOrEqual, value: String(filterModel.dateTo) })
    }
    return criteria
  }

  const operator = mapAgOperatorToBackend(filterModel.type)
  if (operator === FilterOperator.Blank || operator === FilterOperator.NotBlank) {
    return [{ property, operator, value: '' }]
  }

  const rawValue = filterModel.filter ?? filterModel.dateFrom ?? filterModel.value
  if (rawValue === undefined || rawValue === null || rawValue === '') return []

  return [{ property, operator, value: String(rawValue) }]
}

function normalizeGroup(group) {
  if (!group || !group.criteria || !group.criteria.length) return null
  const wire = {
    objectPath: group.objectPath || '',
    logic: group.logic || FilterLogic.And,
    criteria: group.criteria.map((c) => ({
      property: c.property,
      operator: c.operator,
      targetType: c.targetType || null,
      value: c.value !== undefined && c.value !== null ? String(c.value) : '',
    })),
  }

  return wire
}

/** Default transformer used when a column declares no `dynamicFilter`. */
export function defaultFilterTransformer(filterModel, colDef) {
  const { objectPath, property } = resolveServerFieldName(colDef)
  const criteria = buildCriteriaFromSimpleModel(property, filterModel)
  if (!criteria.length) return null
  return { objectPath, targetType: FilterTargetType.Object, logic: FilterLogic.And, criteria }
}

/** Default transformer used when a column declares no `dynamicSort`. */
export function defaultSortTransformer(sortEntry, colDef) {
  const { objectPath, property } = resolveServerFieldName(colDef)
  return { objectPath, property, sortType: sortEntry.sort === 'desc' ? SortType.Desc : SortType.Asc }
}

/**
 * Reusable helper for enum-like columns whose display label is translated
 * client-side. Matches the user's typed text against every raw value's
 * translated label and OR's together an Equals criteria per match.
 *
 *   dynamicFilter: (fm) => buildEnumTranslatedOrGroup({
 *     userText: fm.filter,
 *     rawValues: ['Approved', 'Rejected'],
 *     translate: (raw) => this.$t('sale.factor.states.' + raw.toLowerCase()),
 *     property: 'State',
 *   })
 */
export function buildEnumTranslatedOrGroup({ userText, rawValues, translate, property, objectPath = '', targetType = FilterTargetType.Object }) {
  const needle = userText ? String(userText).trim().toLowerCase() : ''
  if (!needle) return null

  const matches = (rawValues || []).filter((raw) => {
    const label = translate(raw)
    return typeof label === 'string' && label.toLowerCase().includes(needle)
  })
  if (!matches.length) return null

  return {
    objectPath,
    targetType,
    logic: FilterLogic.Or,
    criteria: matches.map((raw) => ({ property, operator: FilterOperator.Equals, value: String(raw) })),
  }
}

/**
 * Reusable helper for date columns (the custom date filter's model:
 * { filter, filter2, filterType: 'date', type }). Opt-in, same pattern as
 * buildEnumTranslatedOrGroup — you only need to supply the property (field)
 * name; 'between' automatically becomes two AND'd criteria.
 *
 *   dynamicFilter: (fm) => buildDateFilterGroup(fm, 'CreatedAt')
 *
 * Pass a third options object only when the date lives on a nested object
 * or inside a Collection:
 *
 *   dynamicFilter: (fm) => buildDateFilterGroup(fm, 'RegisteredAt', {
 *     objectPath: 'Shipments',
 *     targetType: 'Collection',
 *   })
 */
export function buildDateFilterGroup(filterModel, property, { objectPath = '', targetType = FilterTargetType.Object, logic = FilterLogic.And } = {}) {
  const criteria = buildCriteriaFromDateModel(property, filterModel)
  if (!criteria.length) return null
  return { objectPath, targetType, logic, criteria }
}

/**
 * Compiles the active filterModel into DynamicFilterGroup[] (the `filters`
 * field on the request payload — see sample: { filters: [...], sorts: [...] }).
 * Columns without a `dynamicFilter` config are skipped (not defaulted) so
 * the caller can keep routing them through the legacy flat filter object
 * during the gradual migration. Pass `{ skipUnconfigured: false }` once a
 * column (or the whole grid) is fully migrated to also default those.
 */
export function buildDynamicFilterPayload(columnLookup, filterModel, { skipUnconfigured = true } = {}) {
  const filters = []
  const consumedColIds = []

  Object.keys(filterModel || {}).forEach((colId) => {
    const colDef = columnLookup.get(colId)
    if (!colDef) return

    const rawModel = filterModel[colId]
    let result = null

    if (typeof colDef.dynamicFilter === 'function') {
      result = colDef.dynamicFilter(rawModel, colDef)
    } else if (colDef.dynamicFilter && typeof colDef.dynamicFilter === 'object') {
      const declared = colDef.dynamicFilter
      const criteria = buildCriteriaFromSimpleModel(declared.property || colDef.field, rawModel)
      result = criteria.length
        ? { objectPath: declared.objectPath ?? '', targetType: declared.targetType ?? FilterTargetType.Object, logic: declared.logic ?? FilterLogic.And, criteria }
        : null
    } else if (!skipUnconfigured) {
      result = defaultFilterTransformer(rawModel, colDef)
    } else {
      return // unconfigured -> let legacy flat-filter path handle it
    }

    if (!result) return
    consumedColIds.push(colId)

    const groups = Array.isArray(result) ? result : [result]
    groups.forEach((g) => {
      const normalized = normalizeGroup(g)
      if (normalized) filters.push(normalized)
    })
  })

  return { filters, consumedColIds }
}

/** Compiles the active sortModel (in priority order) into DynamicSortCriteria[]. */
export function buildDynamicSortPayload(columnLookup, sortModel) {
  const sorts = []

    ; (sortModel || []).forEach((entry) => {
      const colDef = columnLookup.get(entry.colId)
      if (!colDef) return

      let result
      if (typeof colDef.dynamicSort === 'function') {
        result = colDef.dynamicSort(entry, colDef)
      } else if (colDef.dynamicSort && typeof colDef.dynamicSort === 'object') {
        result = {
          objectPath: colDef.dynamicSort.objectPath ?? '',
          property: colDef.dynamicSort.property ?? colDef.field,
          sortType: entry.sort === 'desc' ? SortType.Desc : SortType.Asc,
        }
      } else {
        result = defaultSortTransformer(entry, colDef)
      }

      if (!result) return
        ; (Array.isArray(result) ? result : [result]).forEach((s) => s && sorts.push(s))
    })

  return sorts
}